import { useState } from 'react'

import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import { useGenerateIncidentSummaryMutation } from '@/graphql/generated/hooks'

import { SummaryPayload } from '../utils/incidentSummary'

/**
 * Generation state shared by the legacy and rebranded summarize modals — the two differ only in
 * how they render it.
 */
export const useIncidentSummary = ({
  incidents,
  onClose,
}: {
  incidents: Array<{ id: string }>
  onClose: () => void
}) => {
  const { t } = useTranslation()
  const { showError } = useToasts()
  const [summaryData, setSummaryData] = useState<SummaryPayload | null>(null)

  const [generateSummary, { loading: isLoading }] =
    useGenerateIncidentSummaryMutation()

  const handleSummarize = async () => {
    try {
      const result = await generateSummary({
        variables: {
          input: { incidentIds: incidents.map((i) => i.id) },
        },
      })
      if (result.data?.generateIncidentSummary) {
        setSummaryData(result.data.generateIncidentSummary)
      }
    } catch (error) {
      // Report the raw failure for diagnosis, but show the operator a translated message rather
      // than backend error text.
      Sentry.captureException(error)
      showError(t('incidents:summary_error'))
    }
  }

  // Generation runs for tens of seconds, so the modal stays put until it settles.
  const handleClose = () => {
    if (!isLoading) {
      setSummaryData(null)
      onClose()
    }
  }

  return { summaryData, isLoading, handleSummarize, handleClose }
}
